import { head } from "lodash";
import { DateTime } from "luxon";

import { OptionalString, UserType } from "./types";
import { USER_GUARDIAN } from "./constants";
import isNil from "lodash/isNil";

type SelectorRoot = HTMLElement | Element | Document;

export const $$ = (
  selector: string,
  root: SelectorRoot = document
): HTMLElement[] => [...root.querySelectorAll<HTMLElement>(selector)];

export const $ = (
  selector: string,
  root: SelectorRoot = document
): HTMLElement | undefined => head($$(selector, root));

export const fail = (message: string) => {
  throw new Error(message);
};

export const localTimezone =
  window.TUTORCHEST.timezone || DateTime.local().zone;

export const localTime = () => DateTime.local().setZone(localTimezone);

export const formattedDate = (date: DateTime): string => {
  return `${date.toFormat("cccc, LLL dd")}${ordinalize(date.day)}`;
};

export const currency = (rate: number | undefined): string | null => {
  if (isNil(rate)) {
    return null;
  }

  return (rate / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD"
  });
};

/**
 *
 * @param n the number to ordinalize
 * @return string
 * @link https://stackoverflow.com/a/31615643/605707
 */
export const ordinalize = (n: number): string => {
  var s = ["th", "st", "nd", "rd"],
    v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

export const hasValue = (value: OptionalString): boolean =>
  value !== undefined && value.trim().length > 0;

export const supportNonGETMethodsOnLinks = () =>
  document.body.addEventListener("click", event => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const el = event.target.closest("[data-method]");

    if (!(el instanceof HTMLElement)) {
      return;
    }

    if (!el.dataset.method) {
      return;
    }

    event.preventDefault();

    const confirmation = el.dataset.confirm;

    if (confirmation && !confirm(confirmation)) {
      return;
    }

    const form = document.createElement("form");

    form.setAttribute("hidden", "hidden");
    form.setAttribute("method", "POST");
    form.setAttribute("action", el.getAttribute("href") || "");

    const csrfInput = document.createElement("input");
    const csrfTokenMeta = $('meta[name="csrf-token"]');

    if (!csrfTokenMeta) {
      throw new Error(
        "No csrf-token meta tag is present; could not submit link form."
      );
    }

    csrfInput.setAttribute("type", "hidden");
    csrfInput.setAttribute("name", "_token");
    csrfInput.setAttribute(
      "value",
      csrfTokenMeta.getAttribute("content") || ""
    );

    const methodInput = document.createElement("input");

    methodInput.setAttribute("type", "hidden");
    methodInput.setAttribute("name", "_method");
    methodInput.setAttribute("value", (el.dataset.method || "").toUpperCase());

    form.appendChild(csrfInput);
    form.appendChild(methodInput);

    document.body.appendChild(form);

    form.submit();
  });

export const disableAutoCompleteOnForms = () => {
  $$("form:not([autocomplete]), input:not([autocomplete])").forEach(element =>
    element.setAttribute("autocomplete", "off")
  );
};

export const displayableUserType = (type: UserType) => {
  if (type === USER_GUARDIAN) {
    return "parent";
  }

  return "tutor";
};

export const nl2br = (str: string) =>
  str.replace(/(\r\n|\n\r|\r|\n)/g, "<br>" + "$1");
